import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import {
  getLocationDetails,
  getLocationsAutocomplete,
  getLocationsAutocompleteSelfDatabase,
  getSearchAutocomplete
} from '../../api/autocomplete'
import { AppDataContext } from '../AppData'
import { PracticeProfile } from '../../types/practice'
import { SpecialistProfile } from '../../types/specialist'
import { ProfileKeyword } from '../../types/keywords'
import {
  AlternativeNavigationSearchTypes,
  AutocompleteSearchTypes,
  HomePageProviderProps,
  HomePageProviderState,
  SearchTypes
} from './types'
import { transformSearchType } from '../../utils/transformAutocomplete'
import { useTimeout } from '../../hooks/useTimeout'

const DEFAULT_LOCATION_ID = 562
const SKIP_PROCEDURE_ID = 152

const HomePageContext = createContext<HomePageProviderState>({
  state: {
    specialistsAutocomplete: [],
    practicesAutocomplete: [],
    specialties: [],
    conditionsProcedures: [],
    locations: [],
    filters: {
      searchType: SearchTypes.specialists,
      searchString: '',
      selectedKeywordName: undefined,
      selectedKeywordSlug: '',
      selectedKeywordId: '',
      selectedLocationName: '',
      selectedLocationId: ''
    },
    isLoadingButtonState: false,
    isKeywordChanged: false,
    isLocationChanged: false,
    isKeywordValidationFailed: false,
    isLocationValidationFailed: false
  },
  resetLocationsAutocomplete: () => null,
  changeFilter: (key, val) => ({ key, val }),
  changeSearch: (search) => search,
  changeLocation: () => {},
  changeLocationSearch: (search) => ({ search }),
  changeSearchType: (searchType) => searchType,
  changeCurrentLocation: (locationId) => locationId,
  openProfilePage: (slug) => slug,
  loadCurrentLocationByLatLon: () => Promise.resolve(),
  buildUrl: () => null,
  validate: () => {},
  resetKeywordSearch: () => {},
  resetValidation: () => {},
  changeButtonLoadingState: (isLoadingButtonState) => isLoadingButtonState,
  isSpecialistSearch: false
})

function HomePageProvider({
  children,
  conditionsAndProcedures,
  specialties,
  defaultLocation,
  locationsAutocomplete,
  practicesAutocomplete,
  specialistsAutocomplete
}: PropsWithChildren<HomePageProviderProps>) {
  const { language: lang, tenantSettings } = useContext(AppDataContext)
  const router = useRouter()

  const defaultLocations = useMemo(() => locationsAutocomplete, [locationsAutocomplete])

  const [state, setState] = useState<HomePageProviderState['state']>({
    specialistsAutocomplete: specialistsAutocomplete || [],
    practicesAutocomplete: practicesAutocomplete || [],
    specialties: specialties || [],
    conditionsProcedures: conditionsAndProcedures,
    locations: locationsAutocomplete || [],
    filters: {
      searchType: SearchTypes.specialists,
      searchString: '',
      selectedKeywordName: undefined,
      selectedKeywordSlug: '',
      selectedKeywordId: '',
      selectedLocationName: '',
      selectedLocationSlug: '',
      selectedLocationId: ''
    },
    isLoadingButtonState: false,
    isKeywordChanged: false,
    isLocationChanged: false,
    isKeywordValidationFailed: false,
    isLocationValidationFailed: false
  })

  const buildUrl = useCallback((): string | null => {
    const validation = validate()

    if (!validation) {
      return null
    }

    const profileType = transformSearchType(state.filters.searchType.toString())
    const isDentistrySelected = profileType === AlternativeNavigationSearchTypes.dentist
    const isNewSearchTypeSelected =
      profileType !== AlternativeNavigationSearchTypes.practices &&
      profileType !== AlternativeNavigationSearchTypes.specialists
    const keywordSlug = isNewSearchTypeSelected ? 'all' : state.filters.selectedKeywordSlug
    const locationName = state.filters.selectedLocationName
    const locationSlug = state.filters.selectedLocationSlug ?? state.filters.selectedLocationName?.toLowerCase()
    const dentistrySlug = tenantSettings?.search?.DENTISTRY?.slug || 'dentistry'

    const hashQueryParams = new Map()

    if (locationName?.length) {
      hashQueryParams.set('location', locationName)
    }

    let hashQueryString = ''

    if (hashQueryParams.size > 0) {
      hashQueryString = Array.from(hashQueryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
    }

    let url = `/${router.locale}/find/${isDentistrySelected ? dentistrySlug : keywordSlug}/${locationSlug}/${
      isDentistrySelected ? AlternativeNavigationSearchTypes.practices : profileType
    }`

    if (hashQueryString) {
      url += `#${hashQueryString}`
    }

    return url
    // eslint-disable-next-line
  }, [state.filters, router.locale])

  const changeFilter = (key: string, value: string | number | boolean | string[]) => {
    const search = tenantSettings?.search
    const dentistrySpecialty = search?.DENTISTRY

    if (key === 'selectedKeywordName' && value === dentistrySpecialty?.name) {
      setState((prev) => ({
        ...prev,
        filters: { ...prev.filters, [key]: value, searchType: SearchTypes.dentist }
      }))
    }

    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: value }
    }))
  }

  const changeButtonLoadingState = (isLoadingButtonState: boolean) => {
    setState((prev) => ({ ...prev, isLoadingButtonState: isLoadingButtonState }))
  }

  const changeLocation = () => {
    setState((prev) => ({ ...prev, isLocationChanged: true }))
  }

  const resetValidation = () => {
    setState((prev) => ({ ...prev, isKeywordValidationFailed: false, isLocationValidationFailed: false }))
  }

  const openProfilePage = (slug: string): void => {
    router.push(
      `/${router.locale}/${state.filters.searchType === SearchTypes.specialists || state.filters.searchType === SearchTypes.healthAndSocialCare ? 'specialist' : 'practice'}/${slug}`
    )
  }

  const conditionProceduresKey = 'conditions-procedures'

  const changeSearch = (search: string) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, searchString: search }
    }))
  }

  const loadAutocomplete = useCallback(async () => {
    let autocomplete: {
      practices: Pick<PracticeProfile, 'id' | 'images' | 'name' | 'slug'>[]
      specialists: Pick<SpecialistProfile, 'id' | 'images' | 'firstName' | 'lastName' | 'slug' | 'keywords'>[]
      specialties: Pick<ProfileKeyword, 'id' | 'iconUri' | 'name' | 'slug' | 'type'>[]
      [conditionProceduresKey]: Pick<ProfileKeyword, 'id' | 'iconUri' | 'name' | 'slug' | 'type'>[]
    }

    switch (state.filters.searchType) {
      case SearchTypes.dentist: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString || 'dentist',
          lang: router.locale as string,
          specialties: [tenantSettings?.search?.DENTISTRY?.id.toString() ?? '0'],
          searchType: AutocompleteSearchTypes.practice
        })
        break
      }
      case SearchTypes.careHome: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString,
          lang: router.locale as string,
          type: SearchTypes.careHome,
          searchType: AutocompleteSearchTypes.practice
        })
        break
      }
      case SearchTypes.hospital: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString,
          lang: router.locale as string,
          type: SearchTypes.hospital,
          searchType: AutocompleteSearchTypes.practice
        })
        break
      }
      case SearchTypes.pharmacy: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString,
          lang: router.locale as string,
          type: SearchTypes.pharmacy,
          searchType: AutocompleteSearchTypes.practice
        })
        break
      }
      case SearchTypes.healthAndSocialCare: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString,
          lang: router.locale as string,
          type: SearchTypes.healthAndSocialCare,
          searchType: AutocompleteSearchTypes.specialist
        })
        break
      }
      case SearchTypes.practices: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString,
          lang: router.locale as string,
          searchType: AutocompleteSearchTypes.practice
        })
        break
      }
      default: {
        autocomplete = await getSearchAutocomplete({
          searchString: state.filters.searchString,
          lang: router.locale as string,
          searchType: AutocompleteSearchTypes.specialist
        })
      }
    }

    setState((prev) => ({
      ...prev,
      practicesAutocomplete: autocomplete?.practices || [],
      specialistsAutocomplete: autocomplete?.specialists || [],
      specialties: autocomplete?.specialties || [],
      conditionsProcedures:
        (defaultLocation
          ? autocomplete?.[conditionProceduresKey]?.filter((x) => x.id !== SKIP_PROCEDURE_ID)
          : autocomplete?.[conditionProceduresKey]) || []
    }))
  }, [
    defaultLocation,
    router.locale,
    state.filters.searchType,
    state.filters.searchString,
    tenantSettings?.search?.DENTISTRY?.id
  ])

  const timeout = useTimeout()

  useEffect(() => {
    timeout(() => loadAutocomplete().then(), 700)
  }, [loadAutocomplete, state.filters.searchString, timeout])

  const loadCurrentLocationByLatLon = async (lat: string, lon: string) =>
    getLocationsAutocomplete({
      lang: router.locale as string,
      lat,
      lon
    }).then((locations) => {
      setState((prev) => ({
        ...prev,
        locations
      }))
    })

  const changeLocationSearch = useCallback(
    async (search: string) => {
      if (search?.length === 0) {
        setState((prev) => ({
          ...prev,
          locations: defaultLocations
        }))
      }
      if (search?.length > 3) {
        let locations: any = []
        setState((prev) => ({
          ...prev,
          locations
        }))
        locations = await getLocationsAutocompleteSelfDatabase({
          search,
          lang: router.locale as string,
          locationId: defaultLocation?.id ?? DEFAULT_LOCATION_ID,
          type: state.filters.searchType
        })
        if (!locations.length) {
          locations = await getLocationsAutocomplete({
            searchString: search,
            lang: router.locale as string,
            language: lang
          })
        }
        setState((prev) => ({
          ...prev,
          locations
        }))
      }
    },
    [defaultLocation, defaultLocations, lang, router.locale, state.filters]
  )

  const resetLocationsAutocomplete = () => {
    setState((prevState) => ({ ...prevState, locations: locationsAutocomplete }))
  }

  const changeCurrentLocation = async (locationId: string, locationName: string, locationSlug?: string) => {
    changeLocation()

    let slug = locationSlug

    if (!locationSlug) {
      const locationDetails: { location: { lat: string; lng: string } } = await getLocationDetails({
        locationId,
        lang: router.locale as string
      })
      const { lat, lng } = locationDetails?.location || { lat: 0, lng: 0 }

      slug = `ext-${lat}-${lng}`
    }

    setState((prev) => ({
      ...prev,
      isLocationChanged: false,
      filters: {
        ...prev.filters,
        selectedLocationName: locationName,
        selectedLocationId: locationId,
        selectedLocationSlug: slug
      }
    }))

    changeLocationSearch(locationName)
  }

  const changeSearchType = async (searchType: SearchTypes) => {
    if (searchType === state.filters.searchType) {
      return
    }

    const isDentistry = searchType === SearchTypes.dentist
    const isNewSearchType =
      searchType !== SearchTypes.specialists &&
      searchType !== SearchTypes.practices &&
      searchType !== SearchTypes.dentist

    if (!isDentistry) {
      resetKeywordSearch()
    }

    if (isDentistry) {
      const search = tenantSettings?.search
      const dentistrySpecialty = search?.DENTISTRY

      setState((prevState) => {
        return {
          ...prevState,
          filters: {
            ...prevState.filters,
            searchType,
            searchString: dentistrySpecialty?.name ?? '',
            selectedKeywordId: dentistrySpecialty?.id.toString() ?? '',
            selectedKeywordName: dentistrySpecialty?.name ?? undefined,
            selectedKeywordSlug: dentistrySpecialty?.slug ?? ''
          }
        }
      })
    } else if (isNewSearchType) {
      setState((prevState) => {
        return {
          ...prevState,
          filters: {
            ...prevState.filters,
            searchType,
            selectedKeywordId: '',
            selectedKeywordName: undefined,
            selectedKeywordSlug: ''
          }
        }
      })
    } else {
      setState((prevState) => {
        return {
          ...prevState,
          filters: {
            ...prevState.filters,
            searchType
          }
        }
      })
    }
  }

  const resetKeywordSearch = () => {
    setState((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        searchString: '',
        selectedKeywordId: '',
        selectedKeywordName: undefined,
        selectedKeywordSlug: ''
      }
    }))
  }

  const validate = useCallback((): boolean => {
    const { selectedKeywordName, selectedLocationName, searchType } = state.filters
    const isNewSearchType =
      searchType !== SearchTypes.specialists &&
      searchType !== SearchTypes.practices &&
      searchType !== SearchTypes.dentist

    setState((prevState) => {
      return {
        ...prevState,
        isKeywordValidationFailed: !isNewSearchType && !selectedKeywordName,
        isLocationValidationFailed: !selectedLocationName
      }
    })

    return !((!isNewSearchType && !selectedKeywordName) || !selectedLocationName)
  }, [state.filters])

  const isSpecialistSearch = useMemo(() => {
    const profileType = state.filters.searchType

    return profileType === SearchTypes.specialists || profileType === SearchTypes.healthAndSocialCare
  }, [state.filters.searchType])

  return (
    <HomePageContext.Provider
      value={{
        state,
        changeSearch,
        changeLocationSearch,
        changeCurrentLocation,
        changeFilter,
        changeLocation,
        changeSearchType,
        openProfilePage,
        resetLocationsAutocomplete,
        loadCurrentLocationByLatLon,
        resetKeywordSearch,
        buildUrl,
        validate,
        resetValidation,
        isSpecialistSearch,
        changeButtonLoadingState
      }}
    >
      {children}
    </HomePageContext.Provider>
  )
}

export { HomePageProvider, HomePageContext }
